/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Sampling", description = "Speed up Customer Studio audience and trait previews by building against a smaller subset of your data.", lastUpdatedDate = new Date(1787702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Sampling",
        description: "Speed up Customer Studio audience and trait previews by building against a smaller subset of your data.",
        lastUpdatedDate: "Wed Aug 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "When to use sampling",
          "depth": 0,
          "id": "when-to-use-sampling"
        }, {
          "value": "How it works",
          "depth": 0,
          "id": "how-it-works"
        }, {
          "value": "Supported warehouses",
          "depth": 0,
          "id": "supported-warehouses"
        }, {
          "value": "Set up sampling",
          "depth": 0,
          "id": "set-up-sampling"
        }, {
          "value": "Check the latest sampling run",
          "depth": 1,
          "id": "check-the-latest-sampling-run"
        }, {
          "value": "Check related model sampling",
          "depth": 0,
          "id": "check-related-model-sampling"
        }, {
          "value": "Use sampled data in a preview",
          "depth": 0,
          "id": "use-sampled-data-in-a-preview"
        }, {
          "value": "Audience previews",
          "depth": 1,
          "id": "audience-previews"
        }, {
          "value": "Trait previews",
          "depth": 1,
          "id": "trait-previews"
        }, {
          "value": "Sampling doesn't change your final audience",
          "depth": 0,
          "id": "sampling-doesnt-change-your-final-audience"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data and platform teams who configure sampling; marketers who use sampled previews"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A defined ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "data schema"
                  })]
                }), _jsxs("li", {
                  children: ["Warehouse ", _jsx(_components.a, {
                    href: "/getting-started/concepts#sources",
                    children: "source"
                  }), " with write permissions"]
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/syncs/lightning-sync-engine",
                    children: "Lightning sync engine"
                  }), " enabled"]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Sampling speeds up audience and trait previews by materializing a small subset of your schema models into your warehouse. It changes previews only, not the full audience Hightouch evaluates when a sync or journey runs."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "when-to-use-sampling",
        children: "When to use sampling"
      }), "\n", _jsx(_components.p, {
        children: "You don't need sampling for every schema. Enable it when previewing audiences or traits against your full dataset is too slow, or uses more warehouse compute than you want during interactive building."
      }), "\n", _jsx(_components.p, {
        children: "Sampling is worth turning on when:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "your parent or related models hold very large datasets"
        }), "\n", _jsx(_components.li, {
          children: "marketers wait too long for audience or trait previews"
        }), "\n", _jsx(_components.li, {
          children: "interactive previews consume significant warehouse compute"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "It gives marketers a faster way to explore audience logic before running it against the full dataset."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "A sampled preview is an approximation. Use it to iterate on audience logic,\nnot to read an exact final count."
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-it-works",
        children: "How it works"
      }), "\n", _jsx(_components.p, {
        children: "Sampling creates smaller copies of your schema models as dedicated tables in your warehouse, then queries those when generating previews:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A configurable percentage of rows is sampled from your ", _jsx(_components.a, {
            href: "/customer-studio/schema#create-the-parent-model",
            children: "parent model"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["For each sampled parent row, the corresponding rows in ", _jsx(_components.a, {
            href: "/customer-studio/schema#add-a-related-model",
            children: "related"
          }), " and ", _jsx(_components.a, {
            href: "/customer-studio/schema#add-an-event-model",
            children: "event models"
          }), " are also materialized."]
        }), "\n", _jsxs(_components.li, {
          children: ["The sampled tables are stored in the ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine#warehouse-schemas",
            children: "Hightouch Planner"
          }), " schema of your warehouse."]
        }), "\n", _jsx(_components.li, {
          children: "Sampling runs on a configurable schedule in the background."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Because each sampled child row ties back to a sampled parent row, previews for ", _jsx(_components.a, {
          href: "/customer-studio/traits",
          children: "traits"
        }), " and ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "audiences"
        }), " can still evaluate relationships while querying far less data."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Sampling requires write access to your warehouse and only works if the\nLightning sync engine is enabled."
        })
      }), "\n", _jsx(_components.h2, {
        id: "supported-warehouses",
        children: "Supported warehouses"
      }), "\n", _jsx(_components.p, {
        children: "Sampling is supported on the following sources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/fabric",
            children: "Fabric"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/sqlserver",
            children: "Microsoft SQL Server"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/trino",
            children: "Trino"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The sampled tables are materialized in the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine#warehouse-schemas",
          children: "Hightouch Planner schema"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "set-up-sampling",
        children: "Set up sampling"
      }), "\n", _jsx(_components.p, {
        children: "Configure sampling from the parent model whose audiences and traits you want to preview."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open a ", _jsx(_components.a, {
            href: "/customer-studio/schema#create-the-parent-model",
            children: "parent model"
          }), " in your schema."]
        }), "\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.strong, {
            children: "Sampling"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Toggle sampling ", _jsx(_components.strong, {
            children: "on"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "center",
              children: "Disabled"
            }), _jsx(_components.th, {
              align: "center",
              children: "Enabled"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "center",
              children: _jsx(_components.img, {
                src: "sampling/disabled.png",
                alt: "Enabling sampling in the Hightouch UI"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: _jsx(_components.img, {
                src: "sampling/enabled.png",
                alt: ""
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Configure the settings, then select ", _jsx(_components.strong, {
            children: "Save & run sampling"
          }), ":"]
        }), "\n"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sample size"
          }), " — The percentage of your parent model (and any related models) that gets sampled."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sample frequency"
          }), " — How often the models get resampled. This schedule also applies to related models unless you override it."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Related models"
          }), " — Lists the related and event models. Select the checkbox next to each related model you want to sample."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "By default, each related model inherits the parent model's sampling schedule. Override it only when a related model needs a different refresh frequency."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sampling/related-sampling.png",
          alt: "Related models table"
        })
      }), "\n", _jsx(_components.h3, {
        id: "check-the-latest-sampling-run",
        children: "Check the latest sampling run"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Last run"
        }), " table shows the status of the most recent run for each sampled model. If a run failed, hover over the status to see the error message. A preview can't use sampled data until its sampling run has completed successfully."]
      }), "\n", _jsx(_components.h2, {
        id: "check-related-model-sampling",
        children: "Check related model sampling"
      }), "\n", _jsxs(_components.p, {
        children: ["You configure related model sampling through the parent model, but you can confirm a related model is being sampled from its own ", _jsx(_components.strong, {
          children: "Sampling"
        }), " tab:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sampling/related-model.png",
          alt: "Sampling configuration for related model"
        })
      }), "\n", _jsx(_components.h2, {
        id: "use-sampled-data-in-a-preview",
        children: "Use sampled data in a preview"
      }), "\n", _jsx(_components.p, {
        children: "After sampling is enabled and at least one run has completed, a sampling toggle appears next to the preview button in the audience and trait builders. Turn it on when you want a faster, approximate preview."
      }), "\n", _jsx(_components.h3, {
        id: "audience-previews",
        children: "Audience previews"
      }), "\n", _jsx(_components.p, {
        children: "With the toggle on, Customer Studio evaluates the audience against the sampled data and marks the result as an approximation."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sampling/audience.png",
          alt: "Sampled audience"
        })
      }), "\n", _jsx(_components.p, {
        children: "Use a sampled preview to check questions such as:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Does the filter logic behave the way I expect?"
        }), "\n", _jsx(_components.li, {
          children: "Am I using the right event, trait, or related model?"
        }), "\n", _jsx(_components.li, {
          children: "Is the audience roughly the size I expected?"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Before a decision that depends on an exact count, preview or run the audience against the full dataset."
      }), "\n", _jsx(_components.h3, {
        id: "trait-previews",
        children: "Trait previews"
      }), "\n", _jsx(_components.p, {
        children: "Traits use the same sampling toggle, next to the trait preview button. Sampling is useful for testing a trait definition quickly before evaluating it across the full parent model."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sampling/trait.png",
          alt: "Sampled trait"
        })
      }), "\n", _jsx(_components.h2, {
        id: "sampling-doesnt-change-your-final-audience",
        children: "Sampling doesn't change your final audience"
      }), "\n", _jsx(_components.p, {
        children: "Sampling is a preview optimization. It doesn't limit an audience to the sampled rows. When Hightouch evaluates the audience for a sync or journey, it uses the full schema data, not the sample."
      }), "\n", _jsx(_components.p, {
        children: "The distinction matters most for marketers: a sampled preview answers \"does this logic look right?\", not \"what is the exact final count?\" Confirm any count-dependent decision against the full dataset before you activate."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/schema#speed-up-previews-with-sampling",
            children: "Define your schema"
          }), " to enable sampling from a model's ", _jsx(_components.strong, {
            children: "Sampling"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " to preview a segment against the sampled model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "Lightning Sync Engine"
          }), " to review the prerequisite that powers sampling, snapshots, and journeys."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
